import React, { useEffect, useState, useRef } from "react";
import { FaFileExcel, FaPrint } from "react-icons/fa";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "./CustomButton";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import Loader from "../dist/Loader";
import formatDate from "../utils/formatDate";
import ExcelJS from "exceljs";

const EmployeeDetails = () => {
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [type, setType] = useState("All");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [employees, setEmployees] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDetails, setShowDetails] = useState(true);
  const [showSummary, setShowSummary] = useState(true);
  const [summaryData, setSummaryData] = useState([]);

  const [counts, setCounts] = useState({
    total: 0,
    working: 0,
    resigned: 0,
    doj: 0,
    left: 0,
  });

  const [userDivision, setUserDivision] = useState(null);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    try {
      const div = localStorage.getItem("division");
      if (div) {
        setUserDivision(div.toString().trim());
      } else {
        const raw = localStorage.getItem("employee_data");
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (parsed?.division)
              setUserDivision(parsed.division.toString().trim());
          } catch (e) { }
        }
      }
    } catch (e) {
      console.warn("Division read error", e);
    }
  }, []);

  useEffect(() => {
    fetch(`${BASE_URL}/api/states`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => setStates(data || []))
      .catch((err) => console.error(err));
  }, []);

  const handleGoClick = () => {
    let url = `${BASE_URL}/api/employees?`;

    if (selectedState) url += `state_id=${selectedState}&`;
    if (type !== "All") {
      url += `status=${type.toUpperCase()}&`;
    }
    if (month && year) {
      url += `month_year=${year}-${month.toString().padStart(2, "0")}&`;
    }

    url = url.endsWith("&") ? url.slice(0, -1) : url;

    setIsLoading(true);
    setError("");
    fetch(url, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        setIsLoading(false);
        setEmployees(data || []);
        setShowTable(true);

        const total = (data || []).length;
        const working = (data || []).filter(
          (emp) => emp.status?.toLowerCase() === "working",
        ).length;
        const resigned = (data || []).filter(
          (emp) => emp.status?.toLowerCase() === "resigned",
        ).length;
        const doj = (data || []).filter((emp) => emp.final_joining_date).length;
        const left = (data || []).filter((emp) => emp.resign_date).length;

        setCounts({ total, working, resigned, doj, left });

        const normalize = (v) =>
          (v || "").toString().trim().replace(/\s+/g, " ");
        const lower = (v) => normalize(v).toLowerCase();

        const normalizeRoleKey = (raw) => {
          const s = (raw || "").toString().trim();
          if (!s) return "UNKNOWN";
          let t = s.replace(/[._]/g, " ").replace(/\s+/g, " ").trim();
          const up = t.toUpperCase();

          if (/^SR[\s\._-]*SO$/.test(up) || /^SENIOR\s+SO$/.test(up)) {
            return "SR SO";
          }
          return up.replace(/\s+/g, " ");
        };

        const wellnessKeywords = [
          "wellness",
          "well",
          "hc",
          "health",
          "healthcare",
          "hc & wl",
        ];
        const cosmaciaKpkbKeywords = ["cosmacia", "kpkb"];

        const allowedWellnessOrder = ["ME", "TSM", "ASM", "RSM", "ZSM", "NSM"];
        const allowedCosmaciaOrder = [
          "BP",
          "SE",
          "SO",
          "SR SO",
          "ASE",
          "TSM",
          "ASM",
          "RSM",
          "ZSM",
          "NSM",
        ];

        const stateRoleMap = {};

        (data || []).forEach((emp) => {
          const state = emp.state_name || "Unknown";
          const roleRaw = emp.designation || "UNKNOWN";
          const role = normalizeRoleKey(roleRaw);
          const status = (emp.status || "").toLowerCase();
          const divisionRaw = emp.division || "Unknown";
          const division = normalize(divisionRaw) || "Unknown";

          if (!stateRoleMap[state]) {
            stateRoleMap[state] = {
              roles: {},
              resignedCount: 0,
              leftCount: 0,
              workingCount: 0,
              total: 0,
              divisionsSeen: {},
            };
          }
          const entry = stateRoleMap[state];

          if (!entry.roles[role]) entry.roles[role] = { total: 0, resigned: 0 };

          entry.roles[role].total += 1;
          if (status === "resigned") {
            entry.roles[role].resigned += 1;
            entry.resignedCount += 1;
          }
          if (emp.resign_date) entry.leftCount += 1;
          if (status === "working") entry.workingCount += 1;
          entry.total += 1;
          entry.divisionsSeen[division] =
            (entry.divisionsSeen[division] || 0) + 1;
        });

        const predominantDivision = (divisionsSeen) => {
          const items = Object.entries(divisionsSeen);
          if (!items.length) return "Unknown";
          items.sort((a, b) => b[1] - a[1]);
          return items[0][0];
        };

        const summary = {};

        Object.entries(stateRoleMap).forEach(([state, meta]) => {
          const rowDivision = predominantDivision(meta.divisionsSeen);
          const divisionLower = lower(rowDivision);

          const userDivLower = userDivision
            ? userDivision.toString().toLowerCase()
            : null;
          const isWellness =
            (userDivLower &&
              wellnessKeywords.some((k) => userDivLower.includes(k))) ||
            (!userDivLower &&
              wellnessKeywords.some((k) => divisionLower.includes(k)));
          const isCosmacia =
            (userDivLower &&
              cosmaciaKpkbKeywords.some((k) => userDivLower.includes(k))) ||
            (!userDivLower &&
              cosmaciaKpkbKeywords.some((k) => divisionLower.includes(k)));

          summary[state] = {};

          if (isWellness) {
            allowedWellnessOrder.forEach((rk) => {
              const obj = meta.roles[rk] || { total: 0, resigned: 0 };
              summary[state][rk] = `${obj.total} (${obj.resigned})`;
            });
            summary[state]["RESIGNED"] = meta.resignedCount || 0;
          } else if (isCosmacia) {
            allowedCosmaciaOrder.forEach((rk) => {
              const obj = meta.roles[rk] || { total: 0, resigned: 0 };
              summary[state][rk] = `${obj.total} (${obj.resigned})`;
            });
            summary[state]["RESIGNED"] = meta.resignedCount || 0;
          } else {
            const allRoles = Object.keys(meta.roles)
              .filter((r) => r !== "UNKNOWN")
              .sort((a, b) => meta.roles[b].total - meta.roles[a].total);

            allRoles.forEach((rk) => {
              const obj = meta.roles[rk] || { total: 0, resigned: 0 };
              summary[state][rk] = `${obj.total} (${obj.resigned})`;
            });
            summary[state]["RESIGNED"] = meta.resignedCount || 0;
          }

          summary[state]["CURRENT FF"] = meta.workingCount || 0;
          summary[state]["TOTAL FF"] = meta.total || 0;
        });

        const formatted = Object.entries(summary).map(([state, roles]) => ({
          state,
          ...roles,
        }));

        const keys = [
          ...new Set(
            formatted.flatMap((r) =>
              Object.keys(r).filter((k) => k !== "state"),
            ),
          ),
        ];

        const grand = { state: "Grand Total" };

        keys.forEach((key) => {
          if (key === "TOTAL FF") {
            grand[key] = formatted.reduce(
              (acc, row) => acc + (Number(row[key]) || 0),
              0,
            );
          } else if (["CURRENT FF", "RESIGNED"].includes(key)) {
            grand[key] = formatted.reduce(
              (acc, row) => acc + (Number(row[key]) || 0),
              0,
            );
          } else {
            let sumTotal = 0;
            let sumResigned = 0;
            formatted.forEach((row) => {
              const cell = row[key];
              if (!cell) return;
              if (typeof cell === "string" && cell.includes("(")) {
                const m = cell.match(/^(\d+)\s*\(\s*(\d+)\s*\)/);
                if (m) {
                  sumTotal += parseInt(m[1], 10);
                  sumResigned += parseInt(m[2], 10);
                } else {
                  const n = parseInt(cell, 10);
                  if (!isNaN(n)) sumTotal += n;
                }
              } else {
                const n = Number(cell);
                if (!isNaN(n)) sumTotal += n;
              }
            });
            grand[key] = `${sumTotal} (${sumResigned})`;
          }
        });

        if (formatted.length) formatted.push(grand);

        setSummaryData(formatted);
      })
      .catch((err) => {
        setIsLoading(false);
        console.error(err);
        setError("Failed to fetch employees");
      });
  };

const exportToExcel = async () => {
  if (!employees.length) return;

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Employee Data");

  ws.columns = [
    { header: "S.No",       key: "sno",        width: 8  },
    { header: "EMP ID",     key: "empId",       width: 15 },
    { header: "Employee",   key: "employee",    width: 30 },
    { header: "State",      key: "state",       width: 18 },
    { header: "Sub State",  key: "subState",    width: 18 },
    { header: "HQ",         key: "hq",          width: 20 },
    { header: "Designation",key: "designation", width: 20 },
    { header: "Division",   key: "division",    width: 18 },
    { header: "Mobile",     key: "mobile",      width: 15 },
    { header: "MGR ID",     key: "mgrId",       width: 12 },
    { header: "Manager",    key: "manager",     width: 20 },
    { header: "Father",     key: "father",      width: 20 },
    { header: "DOB",        key: "dob",         width: 15 },
    { header: "DOJ",        key: "doj",         width: 15 },
    { header: "Confirm",    key: "confirm",     width: 15 },
    { header: "DOR",        key: "dor",         width: 15 },
    { header: "Email",      key: "email",       width: 25 },
    { header: "Address",    key: "address",     width: 40 },
    { header: "Aadhar NO",  key: "aadhar",      width: 18 },
    { header: "PAN NO",     key: "pan",         width: 15 },
    { header: "PF NO",      key: "pf",          width: 15 },
    { header: "PF Date",    key: "pfDate",      width: 15 },
    { header: "UAN NO",     key: "uan",         width: 15 },
    { header: "ESI NO",     key: "esi",         width: 15 },
    { header: "ESI Date",   key: "esiDate",     width: 15 },
    { header: "Status",     key: "status",      width: 12 },
  ];

  // Helper: parse raw date string → Date or ""
  const parseDate = (raw) => {
    if (!raw) return "";
    try {
      const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(String(raw))
        ? `${raw}T00:00:00` : raw);
      return isNaN(d.getTime()) ? "" : d;
    } catch { return ""; }
  };

  employees.forEach((emp, i) => {
    const empRaw = String(emp.employee ?? "").trim();
    const empIsNum = /^\d+$/.test(empRaw);

    const row = ws.addRow({
      sno:         i + 1,
      empId:       empIsNum ? Number(empRaw) : empRaw,
      employee:    `${emp.first_name || ""} ${emp.last_name || ""}`.trim(),
      state:       emp.state_name,
      subState:    emp.substate_name,
      hq:          emp.headquarter_name,
      designation: emp.designation,
      division:    emp.division,
      mobile:      emp.mobile,
      mgrId:       emp.manager_user_id,
      manager:     emp.manager,
      father:      emp.father_name,
      dob:         parseDate(emp.dob),
      doj:         parseDate(emp.final_joining_date),
      confirm:     parseDate(emp.confirm_date),
      dor:         parseDate(emp.resign_date),
      email:       emp.email,
      address:     emp.address,
      aadhar:      emp.aadhar_no,
      pan:         emp.pan_no,
      pf:          emp.pf_no,
      pfDate:      parseDate(emp.pf_date),
      uan:         emp.uan_no,
      esi:         emp.esi_no,
      esiDate:     parseDate(emp.esi_date),
      status:      emp.status,
    });

    // Format empId cell
    row.getCell("empId").numFmt = empIsNum ? "0" : "@";

    // Apply date format to all date cells
    ["dob","doj","confirm","dor","pfDate","esiDate"].forEach((key) => {
      const cell = row.getCell(key);
      if (cell.value instanceof Date) cell.numFmt = "DD-MMM-YYYY";
    });
  });

  // ── Header row styling ──────────────────────────────────────
  ws.getRow(1).eachCell((cell) => {
    cell.font      = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2563EB" } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  });

  // ── All rows: borders + alternating fill ───────────────────
  ws.eachRow((row, rowNumber) => {
    row.height = 20;
    row.eachCell((cell) => {
      cell.border = {
        top:    { style: "thin" },
        left:   { style: "thin" },
        bottom: { style: "thin" },
        right:  { style: "thin" },
      };
      cell.alignment = {
        vertical:   "middle",
        horizontal: rowNumber === 1 ? "center" : "left",
        wrapText:   true,
      };
    });

    if (rowNumber % 2 === 0 && rowNumber !== 1) {
      row.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF5F5F5" } };
      });
    }
  });

  ws.views       = [{ state: "frozen", ySplit: 1 }];
  ws.autoFilter  = { from: "A1", to: "Z1" };

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), "Employee_Details.xlsx");
};

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const tableHtml = tableContainerRef.current?.innerHTML || "";
    const stateName = selectedState
      ? states.find((s) => s.id === parseInt(selectedState))?.name
      : "All";

    if (!printWindow) {
      alert("Please allow popups to enable printing.");
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Employee Details Report</title>
          <style>
            @page { 
              size: landscape; 
              margin: 10mm;
            }
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 20px; 
              color: #111;
            }
            .print-header {
              margin-bottom: 12px;
            }
            .print-title {
              font-size: 18px;
              font-weight: 700;
              margin-bottom: 6px;
            }
            .print-filters {
              font-size: 13px;
              margin-bottom: 12px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-bottom: 20px; 
              font-size: 11px;
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 6px; 
              text-align: left; 
              vertical-align: top;
              word-break: break-word;
              white-space: normal;
            }
            th { 
              background-color: #f2f8fb; 
              font-weight: 700;
              text-align: center;
            }
            tr { 
              page-break-inside: avoid; 
            }
            .status-working { background: #e6ffed; color: #065f46; padding: 4px 6px; border-radius: 3px; display:inline-block; font-weight:600; }
            .status-resigned { background: #fff1f2; color: #9f1239; padding: 4px 6px; border-radius: 3px; display:inline-block; font-weight:600; }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div class="print-title">Employee Details Report</div>
            <div class="print-filters">
              <div><strong>State:</strong> ${stateName}</div>
              <div><strong>Type:</strong> ${type}</div>
              <div><strong>Month:</strong> ${month || "All"}</div>
              <div><strong>Year:</strong> ${year || "All"}</div>
              <div><strong>Generated on:</strong> ${new Date().toLocaleString()}</div>
            </div>
          </div>
          <div class="table-container">
            ${tableHtml}
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                // do not auto-close to allow user to interact if needed
              }, 300);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="p-4 bg-white border border-gray-200 rounded-lg">
      <div className="">
        <div className="EmployeeStateWise-content-container flex flex-col items-center p-4">
          <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-6">
            <span className="text-blue-700"> Field </span>
            <span className="text-blue-500">Force </span>
          </h1>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-4 mb-4 justify-items-center">
            <div className="flex flex-col w-64 sm:w-64">
              <label className="mb-1 text-center">State</label>
              <select
                className="w-full border border-gray-300 rounded px-2 py-1"
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
              >
                <option value="">All</option>
                {states.map((state) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col w-64 sm:w-64">
              <label className="mb-1 text-center">Type</label>
              <select
                className="w-full border border-gray-300 rounded px-2 py-1"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="All">All</option>
                <option value="Working">Working</option>
                <option value="Resigned">Resigned</option>
              </select>
            </div>

            <div className="flex flex-col w-64 sm:w-64">
              <label className="mb-1 text-center">Month:</label>
              <select
                className="w-full border border-gray-300 rounded px-2 py-1"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              >
                <option value="">All</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString("default", {
                      month: "long",
                    })}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col w-64 sm:w-64">
              <label className="mb-1 text-center">Year</label>
              <select
                className="w-full border border-gray-300 rounded px-2 py-1"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              >
                <option value="">All</option>
                {Array.from({ length: 5 }, (_, i) => {
                  const y = new Date().getFullYear() - i;
                  return (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-4 justify-center">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showDetails}
                onChange={() => setShowDetails(!showDetails)}
              />
              Show Details
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showSummary}
                onChange={() => setShowSummary(!showSummary)}
              />
              Show Summary
            </label>
          </div>

          <div className="flex justify-center mt-4">
            <CustomButton
              variant="go"
              onClick={handleGoClick}
              className="mx-auto"
            >
              GO
            </CustomButton>
          </div>
        </div>

        {showTable && (
          <>
            <div className="bg-blue-50 p-4 rounded shadow mb-4 flex flex-wrap gap-4 justify-center">
              <span className="bg-white px-3 py-1 rounded border text-sm">
                <strong>Total:</strong> {counts.total}
              </span>
              <span className="bg-white px-3 py-1 rounded border text-sm">
                <strong>Working:</strong> {counts.working}
              </span>
              <span className="bg-white px-3 py-1 rounded border text-sm">
                <strong>Resigned:</strong> {counts.resigned}
              </span>
              <span className="bg-white px-3 py-1 rounded border text-sm">
                <strong>With DOJ:</strong> {counts.doj}
              </span>
              <span className="bg-white px-3 py-1 rounded border text-sm">
                <strong>Left:</strong> {counts.left}
              </span>
            </div>

            {showDetails && (
              <div
                className="border rounded shadow  table-wrapper"
                ref={tableContainerRef}
              >
                <table className="unlock-dcr-leave-table text-sm ">
                  <thead className="bg-blue-500 text-white">
                    <tr className="bg-sky-100">
                      <th className="px-3 py-2 border text-center">S.No</th>
                      <th className="px-3 py-2 border text-center">EMP ID</th>
                      <th className="px-3 py-2 border text-left">Employee</th>
                      <th className="px-3 py-2 border text-left">State</th>
                      <th className="px-3 py-2 border text-left">Sub State</th>
                      <th className="px-3 py-2 border text-left">HQ</th>
                      <th className="px-3 py-2 border text-left">
                        Designation
                      </th>
                      <th className="px-3 py-2 border text-left">Division</th>
                      <th className="px-3 py-2 border text-center">Mobile</th>
                      <th className="px-3 py-2 border text-center">MGR ID</th>
                      <th className="px-3 py-2 border text-left">Manager</th>
                      <th className="px-3 py-2 border text-left">Father</th>
                      <th className="px-3 py-2 border text-left">DOB</th>
                      <th className="px-3 py-2 border text-left">DOJ</th>
                      <th className="px-3 py-2 border text-left">Confirm</th>
                      <th className="px-3 py-2 border text-left">DOR</th>
                      <th className="px-3 py-2 border text-left">Email</th>
                      <th className="px-3 py-2 border text-left">Address</th>
                      <th className="px-3 py-2 border text-center">
                        Aadhar NO
                      </th>
                      <th className="px-3 py-2 border text-center">PAN NO</th>
                      <th className="px-3 py-2 border text-center">PF NO</th>
                      <th className="px-3 py-2 border text-left">PF Date</th>
                      <th className="px-3 py-2 border text-center">UAN NO</th>
                      <th className="px-3 py-2 border text-center">ESI NO</th>
                      <th className="px-3 py-2 border text-left">ESI Date</th>
                      <th className="px-3 py-2 border text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((emp, index) => (
                      <tr
                        key={emp.id}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-3 py-2 border text-center">
                          {index + 1}
                        </td>
                        <td className="px-3 py-2 border text-center">
                          {emp.employee || "-"}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {(emp.first_name || "") + " " + (emp.last_name || "")}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {emp.state_name || "-"}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {emp.substate_name || "-"}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {emp.headquarter_name || "-"}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {emp.designation || "-"}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {emp.division || "-"}
                        </td>
                        <td className="px-3 py-2 border text-center">
                          {emp.mobile || "-"}
                        </td>
                        <td className="px-3 py-2 border text-center">
                          {emp.manager_user_id || "-"}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {emp.manager || "-"}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {emp.father_name || "-"}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {formatDate(emp.dob) || "-"}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {formatDate(emp.final_joining_date) || "-"}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {emp.confirm_date || "-"}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {emp.resign_date || "-"}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {emp.email || "-"}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {emp.address || "-"}
                        </td>
                        <td className="px-3 py-2 border text-center">
                          {emp.aadhar_no || "-"}
                        </td>
                        <td className="px-3 py-2 border text-center">
                          {emp.pan_no || "-"}
                        </td>
                        <td className="px-3 py-2 border text-center">
                          {emp.pf_no || "-"}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {emp.pf_date || "-"}
                        </td>
                        <td className="px-3 py-2 border text-center">
                          {emp.uan_no || "-"}
                        </td>
                        <td className="px-3 py-2 border text-center">
                          {emp.esi_no || "-"}
                        </td>
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {emp.esi_date || "-"}
                        </td>
                        <td className="px-3 py-2 border text-center">
                          <span
                            className={`px-2 py-1 rounded text-sm font-semibold ${emp.status?.toLowerCase() === "working"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                              }`}
                          >
                            {emp.status || "-"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {showSummary && summaryData.length > 0 && (
              <div className="overflow-x-auto mb-6">
                <h3 className="text-lg font-semibold mb-4 text-center">
                  Employee Count Summary
                </h3>

                <div className="border rounded shadow  table-wrapper">
                  <table className="unlock-dcr-leave-table text-sm ">
                    <thead className="bg-blue-500 text-white">
                      <tr className="bg-blue-100">
                        <th className="px-4 py-2 border">STATE</th>
                        {[
                          ...new Set(
                            summaryData.flatMap((row) =>
                              Object.keys(row).filter((k) => k !== "state"),
                            ),
                          ),
                        ].map((role) => (
                          <th
                            key={role}
                            className="px-4 py-2 border text-center"
                          >
                            {role}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {summaryData.map((row, i) => (
                        <tr
                          key={i}
                          className={
                            row.state === "Grand Total"
                              ? "font-bold bg-gray-100"
                              : ""
                          }
                        >
                          <td className="px-4 py-2 border font-medium">
                            {row.state}
                          </td>
                          {[
                            ...new Set(
                              summaryData.flatMap((r) =>
                                Object.keys(r).filter((k) => k !== "state"),
                              ),
                            ),
                          ].map((role) => (
                            <td
                              key={role}
                              className="px-4 py-2 border text-center"
                            >
                              {row[role] !== undefined ? row[role] : 0}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="mt-4 flex gap-4 justify-center">
              <CustomButton onClick={handlePrint} label="Print" variant="print">
                🖨
              </CustomButton>
              <CustomButton
                onClick={exportToExcel}
                label="Export"
                variant="export"
              >
                📥
              </CustomButton>
            </div>
          </>
        )}

        {isLoading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <Loader onCancel={() => setIsLoading(false)} />
          </div>
        )}

        {error && (
          <div className="mt-4 text-center text-sm text-red-600">{error}</div>
        )}
      </div>
    </div>
  );
};

export default EmployeeDetails;
